import { useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ClipboardCopy,
  Download,
  FileDigit,
  FileUp,
} from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { hexDump, parseHexDump } from '../lib/hexDump';

const FILE_CAP = 2 * 1024 * 1024; // 2 MiB — anything larger gets sluggish.

type SourceMode = 'text' | 'file';

const SAMPLE_TEXT = 'Hello, world!\nThis is a hexdump.\n中文 emoji 😀';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(2)} MiB`;
}

/**
 * `/tools/encode/binary` — hex dump + parser.
 *
 * Two input sources:
 *   - text   — UTF-8 encode the textarea then dump.
 *   - file   — read the dropped / picked file (≤ 2 MiB) then dump.
 *
 * Reverse direction: paste a hex dump (or raw hex pairs) into the
 * "解析回字节" pane and get the original bytes back; we offer
 * a UTF-8 preview and a download.
 */
export default function BinaryTool(): JSX.Element {
  const [src, setSrc] = useState<SourceMode>('text');
  const [text, setText] = useState<string>(SAMPLE_TEXT);
  const [fileBytes, setFileBytes] = useState<Uint8Array | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [columns, setColumns] = useState<8 | 16 | 32>(16);
  const [uppercase, setUppercase] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reverse direction
  const [parseInput, setParseInput] = useState<string>('');
  const parsed = useMemo(() => parseHexDump(parseInput), [parseInput]);

  const inputBytes = useMemo<Uint8Array>(() => {
    if (src === 'text') return new TextEncoder().encode(text);
    return fileBytes ?? new Uint8Array(0);
  }, [src, text, fileBytes]);

  const dumped = useMemo(
    () => hexDump(inputBytes, { columns, uppercase }),
    [inputBytes, columns, uppercase],
  );

  const onPickFile = async (f: File): Promise<void> => {
    setFileError(null);
    if (f.size > FILE_CAP) {
      setFileError(`文件超过 ${fmtBytes(FILE_CAP)} 上限（当前 ${fmtBytes(f.size)}）`);
      setFileBytes(null);
      setFileName(f.name);
      return;
    }
    const buf = await f.arrayBuffer();
    setFileBytes(new Uint8Array(buf));
    setFileName(f.name);
  };

  const onDownloadParsed = (): void => {
    if (parsed.bytes.length === 0) return;
    const blob = new Blob([parsed.bytes as BlobPart], {
      type: 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'parsed.bin';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const utf8Preview = useMemo<string>(() => {
    if (parsed.bytes.length === 0) return '';
    try {
      return new TextDecoder('utf-8', { fatal: false }).decode(parsed.bytes);
    } catch {
      return '';
    }
  }, [parsed.bytes]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="binary"
        category="encode"
        description="hex dump 三栏视图（offset / hex / ASCII）+ 反向解析回字节流。文本输入走 UTF-8 编码；文件输入上限 2 MiB（防止页面卡顿）。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            源
          </span>
          <div className="flex items-center gap-2 text-[11px] text-ink-muted">
            <span>列数：</span>
            {[8, 16, 32].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColumns(c as 8 | 16 | 32)}
                className={clsx(
                  'rounded border px-2 py-0.5 text-[12px] transition',
                  columns === c
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
                )}
              >
                {c}
              </button>
            ))}
            <label className="ml-2 inline-flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={uppercase}
                onChange={(e) => setUppercase(e.target.checked)}
              />
              大写
            </label>
          </div>
        </header>
        <div className="flex gap-1.5">
          <SrcBtn active={src === 'text'} onClick={() => setSrc('text')}>
            <FileDigit className="h-3.5 w-3.5" /> 文本输入
          </SrcBtn>
          <SrcBtn active={src === 'file'} onClick={() => setSrc('file')}>
            <FileUp className="h-3.5 w-3.5" /> 文件输入
          </SrcBtn>
        </div>

        {src === 'text' ? (
          <textarea
            className="input min-h-[100px] resize-y font-mono text-[13px]"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="输入任意文本，下方实时显示 hex dump"
            spellCheck={false}
          />
        ) : (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              className="btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUp className="h-3.5 w-3.5" />
              选择文件…
            </button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickFile(f);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <div className="text-[11px] text-ink-dim">
              {fileName ? (
                <>
                  <span className="font-mono">{fileName}</span>
                  {fileBytes && (
                    <span className="ml-2 badge font-mono">
                      {fmtBytes(fileBytes.byteLength)}
                    </span>
                  )}
                </>
              ) : (
                '尚未选择文件（上限 2 MiB）'
              )}
            </div>
            {fileError && (
              <div className="text-[12px] text-red-500">{fileError}</div>
            )}
          </div>
        )}
      </section>

      <DumpPane
        title="hex dump"
        meta={`${inputBytes.byteLength} 字节 · ${columns} 列${
          uppercase ? ' · 大写' : ''
        }`}
        text={dumped}
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            反向：粘贴 hex dump 解析回字节
          </span>
          <div className="flex items-center gap-2 text-[11px] text-ink-dim">
            <span>{parsed.bytes.length} 字节</span>
            {parsed.errors.length > 0 && (
              <span className="badge border-amber-500/30 text-amber-600 dark:text-amber-400">
                {parsed.errors.length} 行无法解析
              </span>
            )}
            <button
              type="button"
              onClick={onDownloadParsed}
              disabled={parsed.bytes.length === 0}
              className="btn-ghost px-2 py-0.5"
              title="下载为 parsed.bin"
            >
              <Download className="h-3 w-3" />
              下载
            </button>
          </div>
        </header>
        <textarea
          className="input min-h-[120px] resize-y font-mono text-[13px]"
          value={parseInput}
          onChange={(e) => setParseInput(e.target.value)}
          placeholder="粘贴 xxd / hexdump -C 输出，或纯 hex 字符串"
          spellCheck={false}
        />
        {parsed.errors.length > 0 && (
          <ul className="text-[11px] text-amber-600 dark:text-amber-400">
            {parsed.errors.slice(0, 5).map((e) => (
              <li key={e.line}>
                第 {e.line} 行：{e.reason}
              </li>
            ))}
            {parsed.errors.length > 5 && (
              <li>… 还有 {parsed.errors.length - 5} 行被忽略</li>
            )}
          </ul>
        )}
        {utf8Preview && (
          <div className="card-pad bg-bg-soft text-[12px]">
            <div className="text-[10px] uppercase tracking-wide text-ink-dim">
              UTF-8 预览
            </div>
            <code className="block whitespace-pre-wrap break-all font-mono">
              {utf8Preview}
            </code>
          </div>
        )}
      </section>
    </div>
  );
}

function SrcBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[12px] transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function DumpPane({
  title,
  meta,
  text,
}: {
  title: string;
  meta: string;
  text: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <section className="card-pad flex min-h-0 flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">
          {title}
        </span>
        <div className="flex items-center gap-2 text-[11px] text-ink-dim">
          <span>{meta}</span>
          <button
            type="button"
            onClick={onCopy}
            disabled={!text}
            className="btn-ghost px-2 py-0.5"
            title="复制"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                已复制
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3 w-3" />
                复制
              </>
            )}
          </button>
        </div>
      </header>
      <pre
        className="card-pad overflow-x-auto whitespace-pre bg-bg-soft font-mono text-[12.5px] text-ink"
        style={{ maxHeight: '40vh' }}
      >
        {text}
      </pre>
    </section>
  );
}
